import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MdOutlineEdit, MdOutlineVisibility } from 'react-icons/md';
import { Trash2 } from 'lucide-react';
import api from '../services/api';
import { useNotification } from '../context/NotificationContext';
import { useUsers } from '../context/UsersContext';
import { usePermissions } from '../hooks/usePermissions';
import RowValidationErrorsCell from '../components/RowValidationErrorsCell';
import UserTimestamp from '../components/UserTimestamp';
import Tooltip from '../components/Tooltip';
import SearchableDropdown from '../components/SearchableDropdown';
import USDateInput from '../components/USDateInput';
import { formatDateUS } from '../utils/dateUtils';
import * as XLSX from 'xlsx';
import './CheckUpload.css';

const formatDateTime = (dt) => {
  if (!dt) return '-';
  return new Date(dt).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

const formatDateUSSafe = (value) => {
  if (!value) return '-';
  if (typeof value === 'string' && value.includes('-') && value.length >= 10) {
    return formatDateUS(value.slice(0, 10));
  }
  return value;
};

const formatRowNumber = (n) => {
  if (n == null) return '-';
  const num = Number(n);
  return Number.isNaN(num) ? String(n) : num >= 1000 ? String(num) : String(num).padStart(3, '0');
};

const getRowStatusStyle = (rowStatus) => {
  const map = {
    VALID: { background: '#d1fae5', color: '#059669' },
    INVALID: { background: '#fee2e2', color: '#dc2626' },
    PENDING: { background: '#fef3c7', color: '#d97706' },
  };
  return map[rowStatus] || { background: '#f3f4f6', color: '#6b7280' };
};

const getJobStatusStyle = (status) => {
  const map = {
    READY_TO_REVIEW: { background: '#d1fae5', color: '#059669' },
    READY_TO_PROMOTE: { background: '#d1fae5', color: '#059669' },
    CORRECTION_REQUIRED: { background: '#fee2e2', color: '#dc2626' },
    COMPLETED: { background: '#d1fae5', color: '#059669' },
    FAILED: { background: '#fee2e2', color: '#dc2626' },
    PENDING: { background: '#fef3c7', color: '#d97706' },
  };
  return map[status] || { background: '#f3f4f6', color: '#6b7280' };
};

const formatJobStatus = (status) => {
  if (!status) return 'PENDING';
  return status.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
};

const TruncatedText = ({ text, maxLength = 24 }) => {
  if (!text) return <span>-</span>;
  const should = text.length > maxLength;
  const display = should ? text.substring(0, maxLength) + '...' : text;
  return should ? (
    <Tooltip text={text} position="bottom">
      <span className="truncated-text">{display}</span>
    </Tooltip>
  ) : <span>{display}</span>;
};

const humanizeErrorCode = (code) =>
  String(code || '')
    .split('_')
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const parseValidationErrorInfo = (validationErrorInfo) => {
  if (!validationErrorInfo) return {};
  if (typeof validationErrorInfo === 'object') return validationErrorInfo;
  if (typeof validationErrorInfo === 'string') {
    try {
      const parsed = JSON.parse(validationErrorInfo);
      return typeof parsed === 'object' && parsed ? parsed : {};
    } catch (e) {
      return { _raw: validationErrorInfo };
    }
  }
  return {};
};

const formatIssueValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const ErrorsBlock = ({ validationErrors, validationErrorInfo }) => {
  const errors = Array.isArray(validationErrors) ? validationErrors : [];
  const infoMap = parseValidationErrorInfo(validationErrorInfo);
  const hasErrors = errors.length > 0;
  const infoKeys = Object.keys(infoMap).filter((k) => k !== '_raw');
  const hasInfo = infoKeys.length > 0 || Boolean(infoMap._raw);
  if (!hasErrors && !hasInfo) return <span style={{ color: '#9ca3af' }}>—</span>;

  const codes = hasErrors ? errors : infoKeys;
  const extraCodes = infoKeys.filter((k) => !codes.includes(k));
  const orderedCodes = [...codes, ...extraCodes];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px 12px' }}>
        <div style={{ fontSize: '11px', fontWeight: '700', color: '#dc2626', textTransform: 'uppercase', marginBottom: '8px' }}>
          Validation Issues
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {orderedCodes.map((code, i) => {
            const details = infoMap[code];
            const fields = details && typeof details === 'object'
              ? Object.keys(details).map((k) => ({ key: k, label: humanizeErrorCode(k) }))
              : [];
            return (
              <div key={`${code}-${i}`} style={{ background: '#fff', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px' }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '4px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '13px', fontWeight: '700', color: '#991b1b' }}>{humanizeErrorCode(code)}</span>
                  <span style={{ fontSize: '11px', color: '#991b1b', background: '#fee2e2', borderRadius: '999px', padding: '2px 8px' }}>{code}</span>
                </div>
                {fields.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '6px 12px' }}>
                    {fields.map((field) => (
                      <div key={field.key}>
                        <div style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase' }}>{field.label}</div>
                        <div style={{ fontSize: '13px', color: '#111827', fontWeight: 600 }}>
                          {formatIssueValue(details?.[field.key])}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {infoMap._raw && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '6px', padding: '8px 12px' }}>
          <div style={{ fontSize: '11px', fontWeight: '700', color: '#d97706', textTransform: 'uppercase', marginBottom: '4px' }}>Raw Info</div>
          <pre style={{ fontSize: '12px', color: '#92400e', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit' }}>{String(infoMap._raw)}</pre>
        </div>
      )}
    </div>
  );
};

const EMPTY_EDIT = {
  clarificationId: '',
  dateSentToRbo: '',
  depositDate: '',
  batchType: '',
  batchDetails: '',
  insurance: '',
  pageNumber: '',
  checkNumber: '',
  patientName: '',
  patientAccountNumber: '',
  clarificationAmount: '',
  checkAmount: '',
  issueDescription: '',
  rboResponse: '',
  dateCompleted: '',
  status: '',
  usonTeamUserName: '',
  eboticsWorkDate: '',
  clarificationType: '',
  rboWorkDate: '',
  clarificationStatus: '',
  resolvedDate: '',
  openedDate: '',
  dateLastUpdated: '',
};

const FIELD_LABELS = {
  clarificationId: 'Clarification ID',
  dateSentToRbo: 'Date Sent to RBO',
  depositDate: 'Date of Deposit',
  batchType: 'Batch Type',
  batchDetails: 'Batch Details',
  insurance: 'Insurance Carrier',
  pageNumber: 'Page Number',
  checkNumber: 'Check Number',
  patientName: 'Patient Name',
  patientAccountNumber: 'Patient Account #',
  clarificationAmount: 'Amt Not Posted Pending',
  checkAmount: 'Amt of Full Deposit',
  issueDescription: 'USON Team Issue Description',
  rboResponse: 'RBO USON Response',
  dateCompleted: 'Date USON Team Completed',
  status: 'Status',
  usonTeamUserName: 'USON Team User Name',
  eboticsWorkDate: 'eBOTICS Work Date',
  clarificationType: 'Clarification Type',
  rboWorkDate: 'RBO Work Date',
  clarificationStatus: 'Clarification Status',
  resolvedDate: 'Resolved Date',
  openedDate: 'Opened Date',
  dateLastUpdated: 'Date Last Updated',
};

const numOrNull = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

const ClarificationValidation = () => {
  const { showError, showSuccess } = useNotification();
  const { getUserById, getUserName } = useUsers();
  const { can, PERMISSIONS } = usePermissions();

  const canCreate = can(PERMISSIONS.CLARIFICATION_VALIDATION_CREATE);
  const canUpdate = can(PERMISSIONS.CLARIFICATION_VALIDATION_UPDATE);
  const canDelete = can(PERMISSIONS.CLARIFICATION_VALIDATION_DELETE);
  const canExport = can(PERMISSIONS.CLARIFICATION_VALIDATION_REPORT_EXPORT);

  const [dashboardJobs, setDashboardJobs] = useState([]);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobSearch, setJobSearch] = useState('');
  const [dashboardPage, setDashboardPage] = useState(0);
  const [dashboardTotalElements, setDashboardTotalElements] = useState(0);
  const [dashboardTotalPages, setDashboardTotalPages] = useState(0);
  const [dashboardHasMore, setDashboardHasMore] = useState(false);
  const [dashboardPageInputValue, setDashboardPageInputValue] = useState('1');

  const [rows, setRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [rowSearch, setRowSearch] = useState('');
  const [errorSearch, setErrorSearch] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [file, setFile] = useState(null);
  const [jobName, setJobName] = useState('');
  const [practiceCode, setPracticeCode] = useState('');
  const [locationCode, setLocationCode] = useState('');
  const [selectedPracticeId, setSelectedPracticeId] = useState('');
  const [practices, setPractices] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loadingPractices, setLoadingPractices] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [viewingRow, setViewingRow] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [editFormData, setEditFormData] = useState(EMPTY_EDIT);
  const [savingEdit, setSavingEdit] = useState(false);
  const [revalidating, setRevalidating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [deletingRow, setDeletingRow] = useState(false);

  const isProcessing = uploading || savingEdit || revalidating || exporting || deleting || deletingRow;

  const fetchDashboard = useCallback(async (page = 0, searchText = '') => {
    setLoadingDashboard(true);
    try {
      const response = await api.getClarificationValidationJobs({
        page,
        size: 50,
        ...(searchText.trim() ? { jobName: searchText.trim() } : {}),
      });
      setDashboardJobs(response?.items || []);
      const totalPagesCount = response?.totalPages || 0;
      setDashboardTotalElements(response?.totalElements || 0);
      setDashboardTotalPages(totalPagesCount);
      setDashboardHasMore(page < totalPagesCount - 1);
      setDashboardPage(page);
      setDashboardPageInputValue((page + 1).toString());
    } catch (err) {
      showError(err?.data?.message || err?.message || 'Failed to load jobs.');
    } finally {
      setLoadingDashboard(false);
    }
  }, [showError]);

  const fetchJobRows = useCallback(async (jobId, page = 0, rowErrorSearch = '') => {
    setLoadingRows(true);
    try {
      const [jobDetails, response] = await Promise.all([
        api.getClarificationValidationJob(jobId),
        api.getClarificationValidationJobRows(jobId, {
          page,
          size: 50,
          ...(rowErrorSearch.trim() ? { errorSearch: rowErrorSearch.trim() } : {}),
        }),
      ]);
      setSelectedJob(jobDetails);
      setRows(response?.items || []);
      setTotalElements(response?.totalElements || 0);
      setTotalPages(response?.totalPages || 0);
      setCurrentPage(page);
    } catch (err) {
      showError(err?.data?.message || err?.message || 'Failed to load rows.');
    } finally {
      setLoadingRows(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchDashboard(dashboardPage, jobSearch);
  }, [fetchDashboard, dashboardPage, jobSearch]);

  useEffect(() => {
    setDashboardPage(0);
    setDashboardPageInputValue('1');
  }, [jobSearch]);

  useEffect(() => {
    const fetchPractices = async () => {
      setLoadingPractices(true);
      try {
        const data = await api.getAllPractices();
        setPractices((Array.isArray(data) ? data : []).filter((p) => p.isActive !== false));
      } catch (e) {
        setPractices([]);
      } finally {
        setLoadingPractices(false);
      }
    };
    fetchPractices();
  }, []);

  useEffect(() => {
    if (!selectedPracticeId) {
      setLocations([]);
      setLocationCode('');
      return;
    }
    const fetchLocations = async () => {
      setLoadingLocations(true);
      try {
        const data = await api.getPracticeLocations(selectedPracticeId);
        setLocations((Array.isArray(data) ? data : []).filter((l) => l.isActive !== false));
      } catch (e) {
        setLocations([]);
      } finally {
        setLoadingLocations(false);
      }
    };
    fetchLocations();
  }, [selectedPracticeId]);

  const handleSort = (field) => {
    if (sortField === field) setSortDirection((prev) => prev === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDirection('asc'); }
  };

  const SortArrow = ({ field }) => {
    if (sortField !== field) return null;
    return <span style={{ marginLeft: '4px', fontSize: '11px', color: '#6366f1' }}>{sortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  const resetUploadModal = () => {
    setFile(null);
    setJobName('');
    setPracticeCode('');
    setLocationCode('');
    setSelectedPracticeId('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const isValidFileType = (f) =>
    f.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || f.name.toLowerCase().endsWith('.xlsx');

  const processSelectedFile = async (f) => {
    if (!isValidFileType(f)) { showError('Please select an XLSX file'); setFile(null); return; }
    if (f.size > 10 * 1024 * 1024) { showError('Maximum allowed file size is 10 MB.'); setFile(null); return; }
    setFile(f);
  };

  const handleUpload = async () => {
    if (!file) return;
    if (!practiceCode) { showError('Practice is required'); return; }
    if (!locationCode) { showError('Location is required'); return; }
    setUploading(true);
    try {
      const response = await api.createClarificationValidationJob(file, practiceCode, locationCode, jobName || null);
      showSuccess('File uploaded successfully!');
      setShowUploadModal(false);
      resetUploadModal();
      await fetchDashboard(dashboardPage, jobSearch);
      if (response?.job?.jobId) await fetchJobRows(response.job.jobId, 0, '');
    } catch (err) {
      showError(err?.data?.message || err?.message || 'Failed to upload file.');
    } finally {
      setUploading(false);
    }
  };

  const handleRevalidate = async () => {
    if (!selectedJob || !canUpdate) return;
    setRevalidating(true);
    try {
      const updatedJob = await api.revalidateClarificationValidationJob(selectedJob.jobId);
      setSelectedJob(updatedJob);
      showSuccess('Job re-validated successfully!');
      await fetchJobRows(selectedJob.jobId, currentPage, errorSearch);
    } catch (err) {
      showError(err?.data?.message || err?.message || 'Failed to re-validate job.');
    } finally {
      setRevalidating(false);
    }
  };

  const handleExportReport = async () => {
    if (!selectedJob || !canExport) return;
    setExporting(true);
    try {
      const blob = await api.exportClarificationValidationReport(selectedJob.jobId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedJob.jobName || 'ClarificationValidation'}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      showSuccess('Report exported successfully!');
    } catch (err) {
      showError(err?.data?.message || err?.message || 'Failed to export report.');
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!jobToDelete || !canDelete) return;
    setDeleting(true);
    try {
      await api.deleteClarificationValidationJob(jobToDelete.jobId);
      showSuccess(`Job "${jobToDelete.jobName || 'Unnamed Job'}" deleted successfully!`);
      if (selectedJob?.jobId === jobToDelete.jobId) {
        setSelectedJob(null);
        setRows([]);
      }
      await fetchDashboard(dashboardPage, jobSearch);
      setShowDeleteConfirm(false);
      setJobToDelete(null);
    } catch (err) {
      showError(err?.data?.message || err?.message || 'Failed to delete job.');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteRowConfirm = async () => {
    if (!rowToDelete || !selectedJob || !canDelete) return;
    setDeletingRow(true);
    try {
      await api.deleteClarificationValidationRow(selectedJob.jobId, rowToDelete.stagingRowId);
      showSuccess('Row deleted successfully!');
      setRowToDelete(null);
      const updatedJob = await api.getClarificationValidationJob(selectedJob.jobId);
      setSelectedJob(updatedJob);
      await fetchJobRows(selectedJob.jobId, currentPage, errorSearch);
    } catch (err) {
      showError(err?.data?.message || err?.message || 'Failed to delete row.');
    } finally {
      setDeletingRow(false);
    }
  };

  const handleEditRow = (row) => {
    setEditingRow(row);
    setEditFormData({
      clarificationId: row.clarificationId || '',
      dateSentToRbo: row.dateSentToRbo || '',
      depositDate: row.depositDate || '',
      batchType: row.batchType || '',
      batchDetails: row.batchDetails || '',
      insurance: row.insurance || '',
      pageNumber: row.pageNumber || '',
      checkNumber: row.checkNumber || '',
      patientName: row.patientName || '',
      patientAccountNumber: row.patientAccountNumber || '',
      clarificationAmount: row.clarificationAmount ?? '',
      checkAmount: row.checkAmount ?? '',
      issueDescription: row.issueDescription || '',
      rboResponse: row.rboResponse || '',
      dateCompleted: row.dateCompleted || '',
      status: row.status || '',
      usonTeamUserName: row.usonTeamUserName || '',
      eboticsWorkDate: row.eboticsWorkDate || '',
      clarificationType: row.clarificationType || '',
      rboWorkDate: row.rboWorkDate || '',
      clarificationStatus: row.clarificationStatus || '',
      resolvedDate: row.resolvedDate || '',
      openedDate: row.openedDate || '',
      dateLastUpdated: row.dateLastUpdated || '',
    });
  };

  const handleSaveEdit = async () => {
    if (!editingRow || !selectedJob || !canUpdate) return;
    setSavingEdit(true);
    try {
      const payload = {
        clarificationId: editFormData.clarificationId || null,
        dateSentToRbo: editFormData.dateSentToRbo || null,
        depositDate: editFormData.depositDate || null,
        batchType: editFormData.batchType || null,
        batchDetails: editFormData.batchDetails || null,
        insurance: editFormData.insurance || null,
        pageNumber: editFormData.pageNumber || null,
        checkNumber: editFormData.checkNumber || null,
        patientName: editFormData.patientName || null,
        patientAccountNumber: editFormData.patientAccountNumber || null,
        clarificationAmount: numOrNull(editFormData.clarificationAmount),
        checkAmount: numOrNull(editFormData.checkAmount),
        issueDescription: editFormData.issueDescription || null,
        rboResponse: editFormData.rboResponse || null,
        dateCompleted: editFormData.dateCompleted || null,
        status: editFormData.status || null,
        usonTeamUserName: editFormData.usonTeamUserName || null,
        eboticsWorkDate: editFormData.eboticsWorkDate || null,
        clarificationType: editFormData.clarificationType || null,
        rboWorkDate: editFormData.rboWorkDate || null,
        clarificationStatus: editFormData.clarificationStatus || null,
        resolvedDate: editFormData.resolvedDate || null,
        openedDate: editFormData.openedDate || null,
        dateLastUpdated: editFormData.dateLastUpdated || null,
      };
      await api.updateClarificationValidationRow(selectedJob.jobId, editingRow.stagingRowId, payload);
      showSuccess('Row updated and re-validated!');
      setEditingRow(null);
      setEditFormData(EMPTY_EDIT);
      await fetchJobRows(selectedJob.jobId, currentPage, errorSearch);
    } catch (err) {
      showError(err?.data?.message || err?.message || 'Failed to update row.');
    } finally {
      setSavingEdit(false);
    }
  };

  const displayedRows = useMemo(() => {
    let list = rows;
    if (!rowSearch.trim()) list = rows;
    else {
    const q = rowSearch.trim().toLowerCase();
      list = rows.filter((r) => {
      const text = [
        r.sheetRowNumber,
        r.rowStatus,
        r.clarificationId,
        r.checkNumber,
        r.patientName,
        r.patientAccountNumber,
        r.status,
        r.clarificationType,
        r.clarificationStatus,
        ...(r.validationErrors || []),
      ].filter(Boolean).join(' ').toLowerCase();
      return text.includes(q);
    });
    }
    if (!sortField) return list;
    const numericFields = ['sheetRowNumber', 'clarificationAmount', 'checkAmount'];
    return [...list].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (numericFields.includes(sortField)) {
        aVal = Number(aVal ?? 0);
        bVal = Number(bVal ?? 0);
      } else {
        aVal = (aVal ?? '').toString().toLowerCase();
        bVal = (bVal ?? '').toString().toLowerCase();
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [rows, rowSearch, sortField, sortDirection]);

  const handleDashboardPageInputChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d+$/.test(value)) {
      setDashboardPageInputValue(value);
    }
  };

  const handleDashboardGoToPage = () => {
    if (dashboardPageInputValue === '' || loadingDashboard) return;
    const pageNum = parseInt(dashboardPageInputValue, 10);
    const maxPage = dashboardTotalPages > 0 ? dashboardTotalPages - 1 : 0;
    if (pageNum >= 1 && pageNum <= maxPage + 1) {
      setDashboardPage(pageNum - 1);
    } else {
      setDashboardPageInputValue((dashboardPage + 1).toString());
    }
  };

  const handleDashboardPageInputKeyPress = (e) => {
    if (e.key === 'Enter') handleDashboardGoToPage();
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) await processSelectedFile(dropped);
  };

  const handleDownloadTemplate = () => {
    const headers = [
      'Clarification Id',
      'DATE SENT to RBO',
      'DATE OF DEPOSIT',
      'BATCH TYPE',
      'BATCH DETAILS',
      'INSURANCE CARRIER',
      'PAGE NUMBER',
      'CHECK NUMBER',
      'PATIENT FNAME/LAST NAME',
      'PATIENT ACCT #',
      'AMT NOT POSTED $ PENDING',
      'AMT OF FULL DEPOSIT',
      'USON Team ISSUE DESCRIPTION (Should indicate "Initial Date sent | User Initials: Issue")',
      'RBO USON RESPONSE (Should indicate "Today\'s Date sent | User Initials: How to resolve")',
      'DATE USON Team COMPLETED',
      'Status',
      'USON TEAM USER NAME',
      'eBOTICS WORK DATE',
      'Clarification type',
      'RBO WORK DATE',
      'clarification status',
      'resolved date',
      'opened date',
      'date last updated',
    ];

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([headers, new Array(headers.length).fill('')]);
    ws['!cols'] = headers.map(() => ({ wch: 28 }));
    XLSX.utils.book_append_sheet(wb, ws, 'Clarification Template');
    XLSX.writeFile(wb, 'ClarificationValidation_Template.xlsx');
  };

  const practiceOptions = [
    { value: '', label: 'Select Practice' },
    ...practices.map((p) => ({ value: p.practiceId || p.id, label: `${p.name} (${p.code})`, code: p.code })),
  ];
  const locationOptions = [
    { value: '', label: 'Select Location' },
    ...locations.map((l) => ({ value: l.code, label: `${l.name} (${l.code})` })),
  ];

  return (
    <div className="check-upload-page">
      {!selectedJob && (
        <div className="page-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <h1 className="page-title" style={{ margin: 0 }}>Clarification Validation</h1>
            <p className="page-subtitle" style={{ margin: 0 }}>Validate clarification sheets against system clarifications</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button className="btn-cancel" onClick={handleDownloadTemplate} disabled={isProcessing}
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                <path d="M3 17C3 18.1046 3.89543 19 5 19H15C16.1046 19 17 18.1046 17 17V13M13 10L10 13M10 13L7 10M10 13V1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Download Template
            </button>
            <Tooltip text="Download the Clarification Validation XLSX template with required columns and sequence" position="bottom">
              <div className="info-icon-wrapper">
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                  <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M10 14V10M10 6H10.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
            </Tooltip>
            {canCreate && (
              <button className="btn-upload-small" onClick={() => setShowUploadModal(true)} disabled={isProcessing}
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                  <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 13H8M12 9V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Upload Clarification Sheet
              </button>
            )}
            <Tooltip text="Upload an XLSX sheet to validate clarification rows against system clarifications" position="bottom">
              <div className="info-icon-wrapper">
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                  <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M10 14V10M10 6H10.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
            </Tooltip>
          </div>
        </div>
      )}

      {showUploadModal && (
        <div className="modal-overlay" onClick={() => { if (!uploading) { setShowUploadModal(false); resetUploadModal(); } }}>
          <div className="upload-modal-content" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#111827' }}>Upload Clarification Validation File</h2>
              <button onClick={() => { if (!uploading) { setShowUploadModal(false); resetUploadModal(); } }} disabled={uploading}
                style={{ background: 'none', border: 'none', fontSize: '24px', color: '#6b7280', cursor: uploading ? 'not-allowed' : 'pointer', padding: '0', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px' }}>×</button>
            </div>
            <div className={`upload-zone ${dragActive ? 'drag-active' : ''} ${file ? 'has-file' : ''}`}
              onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}>
              <input ref={fileInputRef} type="file" id="cv-file-input" accept=".xlsx" onChange={async (e) => {
                const selected = e.target.files?.[0];
                if (selected) await processSelectedFile(selected);
                e.target.value = '';
              }} style={{ display: 'none' }} />
              {!file ? (
                <>
                  <div className="upload-text"><strong>Click to select XLSX file</strong><span>or drag and drop here (max 10 MB)</span></div>
                  <label htmlFor="cv-file-input" className="upload-label">Select File</label>
                </>
              ) : (
                <div className="file-selected">
                  <div className="file-info"><strong>{file.name}</strong><span>{(file.size / 1024).toFixed(2)} KB</span></div>
                  <button className="btn-remove" onClick={() => setFile(null)} disabled={uploading}>Remove</button>
                </div>
              )}
            </div>
            {file && (
              <div style={{ marginTop: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>Job Name (Optional)</label>
                <input
                  type="text"
                  value={jobName}
                  onChange={(e) => setJobName(e.target.value)}
                  placeholder="Enter job name"
                  style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', boxSizing: 'border-box' }}
                />
                <div style={{ marginTop: '12px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                    Practice <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <SearchableDropdown
                    options={loadingPractices ? [{ value: '', label: 'Loading...' }] : practiceOptions}
                    value={selectedPracticeId}
                    onChange={(val) => {
                      setSelectedPracticeId(val);
                      const found = practices.find((p) => (p.practiceId || p.id) === val);
                      setPracticeCode(found?.code || '');
                      setLocationCode('');
                    }}
                    placeholder="Select Practice"
                  />
                </div>
                <div style={{ marginTop: '12px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                    Location <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <SearchableDropdown
                    options={loadingLocations ? [{ value: '', label: 'Loading...' }] : locationOptions}
                    value={locationCode}
                    onChange={setLocationCode}
                    placeholder={!selectedPracticeId ? 'Select practice first' : 'Select Location'}
                    disabled={!selectedPracticeId || loadingLocations}
                  />
                </div>
                <div className="upload-actions">
                  <button className="btn-cancel" onClick={() => { setShowUploadModal(false); resetUploadModal(); }} disabled={uploading}>Cancel</button>
                  <button className="btn-upload" onClick={handleUpload} disabled={uploading || !file || !practiceCode || !locationCode}>
                    {uploading ? 'Uploading...' : 'Upload & Validate'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {!selectedJob && (
        <>
          <div className="filters-section jobs-search-section">
            <div className="search-bar">
              <input type="text" value={jobSearch} onChange={(e) => setJobSearch(e.target.value)} placeholder="Search by job name or file name..." />
            </div>
          </div>
          <div className="jobs-section" style={{ marginTop: 0 }}>
            <h2 className="section-title">Clarification Validation Jobs</h2>
            {loadingDashboard ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>Loading jobs...</div>
            ) : dashboardJobs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>No jobs found</div>
            ) : (
              <div className="jobs-table-wrapper">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <button className="btn-cancel" disabled={dashboardPage === 0 || loadingDashboard} onClick={() => setDashboardPage((p) => Math.max(0, p - 1))} title="Previous page" aria-label="Previous page" style={{ padding: '6px 10px', minWidth: '34px' }}>
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                      <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <div style={{ fontSize: '13px', color: '#6b7280' }}>
                    Showing {dashboardTotalElements === 0 ? 0 : dashboardPage * 50 + 1} - {Math.min((dashboardPage + 1) * 50, dashboardTotalElements)} of {dashboardTotalElements}
                  </div>
                  {dashboardTotalPages > 1 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>Page:</span>
                      <input
                        type="text"
                        value={dashboardPageInputValue}
                        onChange={handleDashboardPageInputChange}
                        onKeyPress={handleDashboardPageInputKeyPress}
                        onBlur={handleDashboardGoToPage}
                        style={{ width: '46px', height: '26px', border: '1px solid #d1d5db', borderRadius: '4px', textAlign: 'center', fontSize: '12px' }}
                      />
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>of {dashboardTotalPages}</span>
                    </div>
                  )}
                  <button className="btn-cancel" disabled={!dashboardHasMore || loadingDashboard} onClick={() => setDashboardPage((p) => p + 1)} title="Next page" aria-label="Next page" style={{ padding: '6px 10px', minWidth: '34px' }}>
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                      <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
                <table className="jobs-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['Job Name', 'File Name', 'Status', 'Total', 'Valid', 'Invalid', 'Created', 'Updated', 'Actions'].map((h) => (
                        <th key={h} style={{ padding: '12px', textAlign: 'left' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardJobs.map((job) => (
                      <tr key={job.jobId} style={{ borderBottom: '1px solid #e5e7eb', cursor: 'pointer' }} onClick={() => fetchJobRows(job.jobId, 0, '')}>
                        <td style={{ padding: '12px' }}><TruncatedText text={job.jobName || 'Unnamed Job'} /></td>
                        <td style={{ padding: '12px' }}><TruncatedText text={job.uploadedFileName || '-'} /></td>
                        <td style={{ padding: '12px' }}>
                          <span className="job-status-badge" style={getJobStatusStyle(job.status)}>{formatJobStatus(job.status)}</span>
                        </td>
                        <td style={{ padding: '12px' }}>{job.totalRows || 0}</td>
                        <td style={{ padding: '12px', color: '#059669' }}>{job.validRows || 0}</td>
                        <td style={{ padding: '12px', color: '#dc2626' }}>{job.invalidRows || 0}</td>
                        <td style={{ padding: '12px' }}><UserTimestamp userId={job.createdBy} dateTime={job.createdAt} action="created" getUserName={getUserName} getUserById={getUserById} /></td>
                        <td style={{ padding: '12px' }}><UserTimestamp userId={job.updatedBy} dateTime={job.updatedAt} action="updated" getUserName={getUserName} getUserById={getUserById} /></td>
                        <td style={{ padding: '12px' }}>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button style={{ padding: '6px 8px', display: 'flex', alignItems: 'center', background: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', color: '#374151' }}
                              onClick={(e) => { e.stopPropagation(); fetchJobRows(job.jobId, 0, ''); }} title="View">
                              <MdOutlineVisibility size={16} />
                            </button>
                            {canDelete && (
                              <button style={{ padding: '6px 8px', color: '#dc2626', border: '1px solid #dc2626', background: 'white', display: 'flex', alignItems: 'center', borderRadius: '6px' }}
                                onClick={(e) => { e.stopPropagation(); setJobToDelete(job); setShowDeleteConfirm(true); }} disabled={isProcessing} title="Delete job">
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {selectedJob && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0 }}>
              <button className="btn-cancel" onClick={() => { setSelectedJob(null); setRows([]); setRowSearch(''); setErrorSearch(''); }} disabled={isProcessing}>← Back</button>
              <div style={{ minWidth: 0 }}>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {selectedJob.jobName || 'Unnamed Job'}
                </h2>
                <p style={{ margin: '2px 0 0 0', color: '#6b7280', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {selectedJob.uploadedFileName} • Created {formatDateTime(selectedJob.createdAt)}
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              {canExport && (
                <button className="btn-cancel" onClick={handleExportReport} disabled={isProcessing}>
                  {exporting ? 'Exporting...' : 'Export Report'}
                </button>
              )}
              {canUpdate && (
                <button className="btn-primary" onClick={handleRevalidate} disabled={isProcessing}>
                  {revalidating ? 'Re-validating...' : 'Re-validate'}
                </button>
              )}
            </div>
          </div>

          {selectedJob.failureReason && (
            <div style={{ marginBottom: '16px', padding: '12px', background: '#fee2e2', border: '1px solid #fecaca', borderRadius: '6px', color: '#dc2626', fontSize: '14px' }}>
              <strong>Failure Reason:</strong> {selectedJob.failureReason}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '16px' }}>
            <div style={{ padding: '16px', background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }}><div style={{ fontSize: '12px', color: '#6b7280' }}>Total</div><div style={{ fontSize: '24px' }}>{selectedJob.totalRows || 0}</div></div>
            <div style={{ padding: '16px', background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }}><div style={{ fontSize: '12px', color: '#6b7280' }}>Valid</div><div style={{ fontSize: '24px', color: '#059669' }}>{selectedJob.validRows || 0}</div></div>
            <div style={{ padding: '16px', background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }}><div style={{ fontSize: '12px', color: '#6b7280' }}>Invalid</div><div style={{ fontSize: '24px', color: '#dc2626' }}>{selectedJob.invalidRows || 0}</div></div>
          </div>

          <div className="filters-section bulk-job-detail-filters">
            <div className="search-bar">
              <input type="text" value={rowSearch} onChange={(e) => setRowSearch(e.target.value)} placeholder="Search rows..." />
            </div>
            <div className="search-bar">
              <input type="text" value={errorSearch} onChange={(e) => setErrorSearch(e.target.value)} placeholder="Filter by validation error..." />
              <button className="btn-cancel" onClick={() => fetchJobRows(selectedJob.jobId, 0, errorSearch)} disabled={loadingRows}>Apply</button>
              {errorSearch.trim() && (
                <button className="btn-cancel" onClick={() => { setErrorSearch(''); fetchJobRows(selectedJob.jobId, 0, ''); }} disabled={loadingRows}>Clear</button>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
            <div style={{ fontSize: '13px', color: '#6b7280' }}>Showing {currentPage * 50 + 1} - {Math.min((currentPage + 1) * 50, totalElements)} of {totalElements}</div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="btn-cancel" disabled={currentPage === 0 || loadingRows} onClick={() => fetchJobRows(selectedJob.jobId, currentPage - 1, errorSearch)}>Previous</button>
              <button className="btn-cancel" disabled={currentPage >= totalPages - 1 || loadingRows} onClick={() => fetchJobRows(selectedJob.jobId, currentPage + 1, errorSearch)}>Next</button>
            </div>
          </div>

          <div className="checks-table-wrapper" style={{ overflowX: 'auto' }}>
            <table className="checks-table" style={{ minWidth: '1600px' }}>
              <thead>
                <tr>
                  {[
                    { label: 'Row', field: 'sheetRowNumber' },
                    { label: 'Row Status', field: 'rowStatus' },
                    { label: 'Clarification ID', field: 'clarificationId' },
                    { label: 'Check #', field: 'checkNumber' },
                    { label: 'Patient', field: 'patientName' },
                    { label: 'Account #', field: 'patientAccountNumber' },
                    { label: 'Clarification Amount', field: 'clarificationAmount' },
                    { label: 'Check Amount', field: 'checkAmount' },
                    { label: 'Type', field: 'clarificationType' },
                    { label: 'Status', field: 'status' },
                    { label: 'Clarification Status', field: 'clarificationStatus' },
                    { label: 'Errors', field: null },
                    { label: 'Actions', field: null },
                  ].map(({ label, field }) => (
                    <th key={label}
                      onClick={field ? () => handleSort(field) : undefined}
                      style={{ padding: '12px', textAlign: 'left', cursor: field ? 'pointer' : 'default', userSelect: 'none' }}>
                      {label}{field && <SortArrow field={field} />}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loadingRows ? (
                  <tr><td colSpan={13} style={{ textAlign: 'center', padding: '40px' }}>Loading rows...</td></tr>
                ) : displayedRows.length === 0 ? (
                  <tr><td colSpan={13} style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>No rows found</td></tr>
                ) : displayedRows.map((row) => (
                  <tr key={row.stagingRowId} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '12px' }}>{formatRowNumber(row.sheetRowNumber)}</td>
                    <td style={{ padding: '12px' }}><span className="job-status-badge" style={getRowStatusStyle(row.rowStatus)}>{row.rowStatus || 'UNKNOWN'}</span></td>
                    <td style={{ padding: '12px' }}><TruncatedText text={row.clarificationId} maxLength={18} /></td>
                    <td style={{ padding: '12px' }}>{row.checkNumber || '-'}</td>
                    <td style={{ padding: '12px' }}><TruncatedText text={row.patientName} /></td>
                    <td style={{ padding: '12px' }}>{row.patientAccountNumber || '-'}</td>
                    <td style={{ padding: '12px' }}>{row.clarificationAmount ?? '-'}</td>
                    <td style={{ padding: '12px' }}>{row.checkAmount ?? '-'}</td>
                    <td style={{ padding: '12px' }}><TruncatedText text={row.clarificationType} maxLength={18} /></td>
                    <td style={{ padding: '12px' }}><TruncatedText text={row.status} maxLength={18} /></td>
                    <td style={{ padding: '12px' }}><TruncatedText text={row.clarificationStatus} maxLength={18} /></td>
                    <td style={{ padding: '12px', maxWidth: '240px' }}>
                      <RowValidationErrorsCell validationErrors={row.validationErrors} validationErrorInfo={row.validationErrorInfo} />
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button title="View details" style={{ padding: '5px 7px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', color: '#374151', cursor: 'pointer' }} onClick={() => setViewingRow(row)}><MdOutlineVisibility size={15} /></button>
                        {canUpdate && (
                          <button title="Edit row" style={{ padding: '5px 7px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', color: '#2563eb', cursor: isProcessing ? 'not-allowed' : 'pointer' }} onClick={() => handleEditRow(row)} disabled={isProcessing}><MdOutlineEdit size={15} /></button>
                        )}
                        {canDelete && (
                          <button title="Delete row" style={{ padding: '5px 7px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'white', border: '1px solid #fecaca', borderRadius: '6px', color: '#dc2626', cursor: isProcessing ? 'not-allowed' : 'pointer' }} onClick={() => setRowToDelete(row)} disabled={isProcessing}><Trash2 size={15} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {viewingRow && (
        <div className="modal-overlay" onClick={() => setViewingRow(null)}>
          <div className="upload-modal-content" style={{ maxWidth: '980px' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Clarification Row Details</h3>
            {(viewingRow.validationErrors?.length > 0 || viewingRow.validationErrorInfo) && (
              <div style={{ marginTop: '12px', marginBottom: '14px' }}>
                <ErrorsBlock validationErrors={viewingRow.validationErrors} validationErrorInfo={viewingRow.validationErrorInfo} />
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '10px 16px', marginTop: '12px' }}>
              {Object.entries({
                'Row Number': formatRowNumber(viewingRow.sheetRowNumber),
                'Clarification ID': viewingRow.clarificationId,
                'Check Number': viewingRow.checkNumber,
                'Patient Name': viewingRow.patientName,
                'Patient Account Number': viewingRow.patientAccountNumber,
                'Date Sent To RBO': formatDateUSSafe(viewingRow.dateSentToRbo),
                'Deposit Date': formatDateUSSafe(viewingRow.depositDate),
                'Batch Type': viewingRow.batchType,
                'Batch Details': viewingRow.batchDetails,
                Insurance: viewingRow.insurance,
                'Page Number': viewingRow.pageNumber,
                'Clarification Amount': viewingRow.clarificationAmount,
                'Check Amount': viewingRow.checkAmount,
                'Issue Description': viewingRow.issueDescription,
                'RBO Response': viewingRow.rboResponse,
                'Date Completed': formatDateUSSafe(viewingRow.dateCompleted),
                Status: viewingRow.status,
                'USON Team User': viewingRow.usonTeamUserName,
                'Ebotics Work Date': formatDateUSSafe(viewingRow.eboticsWorkDate),
                'Clarification Type': viewingRow.clarificationType,
                'RBO Work Date': formatDateUSSafe(viewingRow.rboWorkDate),
                'Clarification Status': viewingRow.clarificationStatus,
                'Resolved Date': formatDateUSSafe(viewingRow.resolvedDate),
                'Opened Date': formatDateUSSafe(viewingRow.openedDate),
                'Date Last Updated': formatDateUSSafe(viewingRow.dateLastUpdated),
                'System Clarification ID': viewingRow.systemClarificationId,
                'Row Status': viewingRow.rowStatus,
                'Last Validated At': formatDateTime(viewingRow.lastValidatedAt),
              }).map(([label, value]) => (
                <div key={label}>
                  <div style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase' }}>{label}</div>
                  <div style={{ fontSize: '13px', color: '#111827' }}>{value || '—'}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: '14px', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn-cancel" onClick={() => setViewingRow(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {editingRow && (
        <div className="modal-overlay">
          <div className="upload-modal-content" style={{ maxWidth: '1040px' }}>
            <h3 style={{ marginTop: 0 }}>Edit Clarification Row</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '10px 16px' }}>
              {Object.entries(EMPTY_EDIT).map(([field]) => {
                const isNumber = field === 'clarificationAmount' || field === 'checkAmount';
                const isDate = field.toLowerCase().includes('date');
                return (
                  <div key={field}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#374151' }}>
                      {FIELD_LABELS[field] || field}
                    </label>
                    {isDate ? (
                      <USDateInput
                        name={field}
                        value={editFormData[field]}
                        onChange={(e) => setEditFormData((prev) => ({ ...prev, [field]: e.target.value }))}
                      />
                    ) : (
                      <input
                        type={isNumber ? 'number' : 'text'}
                        step={isNumber ? '0.01' : undefined}
                        value={editFormData[field]}
                        onChange={(e) => setEditFormData((prev) => ({ ...prev, [field]: e.target.value }))}
                        style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button className="btn-cancel" onClick={() => { setEditingRow(null); setEditFormData(EMPTY_EDIT); }} disabled={savingEdit}>Cancel</button>
              <button className="btn-upload" onClick={handleSaveEdit} disabled={savingEdit}>{savingEdit ? 'Saving...' : 'Save & Re-validate'}</button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="upload-modal-content" style={{ maxWidth: '420px' }}>
            <h3 style={{ marginTop: 0, color: '#dc2626' }}>Confirm Delete Job</h3>
            <p>Delete job "<strong>{jobToDelete?.jobName || 'Unnamed Job'}</strong>"? This removes all staged rows permanently.</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button className="btn-cancel" onClick={() => { setShowDeleteConfirm(false); setJobToDelete(null); }}>Cancel</button>
              <button style={{ padding: '8px 20px', background: '#dc2626', border: 'none', borderRadius: '6px', color: 'white' }} onClick={handleDeleteConfirm} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {rowToDelete && (
        <div className="modal-overlay">
          <div className="upload-modal-content" style={{ maxWidth: '420px' }}>
            <h3 style={{ marginTop: 0, color: '#dc2626' }}>Delete Row</h3>
            <p style={{ color: '#374151' }}>Permanently delete this staged row?</p>
            <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
              Row {formatRowNumber(rowToDelete.sheetRowNumber)} — Clarification ID: {rowToDelete.clarificationId || '—'}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button className="btn-cancel" onClick={() => setRowToDelete(null)} disabled={deletingRow}>Cancel</button>
              <button style={{ padding: '8px 20px', background: '#dc2626', border: 'none', borderRadius: '6px', color: 'white' }} onClick={handleDeleteRowConfirm} disabled={deletingRow}>
                {deletingRow ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClarificationValidation;
